import { useRef } from 'react';

import { UpdateWorkspaceBrandingRequestDTO } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import QuestionIcon from '@/components/icons/question';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useToast } from '@/components/ui/toaster';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { workspaceSwitcherKeys } from '@/queries/workspace-switcher';
import { ApiService } from '@/services/api-v2';
import { useUserStore } from '@/stores/user.store';
import { convertLogoUrl } from '@/utils/image-url';

const formSchema = z.object({
  logoUrl: z.string(),
  color: z.string(),
});

function SettingsBrandingPage() {
  return (
    <div className="flex w-full max-w-[833px] flex-col gap-4 rounded-lg bg-white p-6 shadow-lg">
      <ScrollArea viewportClassName="pr-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-default-text text-xl font-medium">Branding</h1>
          <SettingsBrandingForm />
        </div>
      </ScrollArea>
    </div>
  );
}

function SettingsBrandingForm() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { org, setOrg } = useUserStore();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { canEditWorkspaceSettings } = usePermissions();

  const formInstance = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      logoUrl: org?.logoUrl ?? '',
      color: org?.brandColor || '#000000',
    },
  });
  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting, isDirty },
  } = formInstance;

  const { mutateAsync: updateWorkspaceBranding } = useMutation({
    mutationFn: (payload: UpdateWorkspaceBrandingRequestDTO) => {
      return ApiService.UpdateWorkspaceBranding({
        workspaceId: org!.id,
        color: payload.color,
        file: payload.file,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceSwitcherKeys.all });
      toast.success('Workspace branding updated successfully');
    },
    onError: () => {
      toast.error('Failed to update workspace branding');
    },
  });

  async function onSubmit(data: z.infer<typeof formSchema>) {
    const isDataUrl = typeof data.logoUrl === 'string' && data.logoUrl.startsWith('data:image/');
    const response = await updateWorkspaceBranding({ color: data.color, file: isDataUrl ? data.logoUrl : null });

    const { logoUrl, brandColor } = await response.json<{ logoUrl: string; brandColor: string }>();
    setOrg({ ...(org as NonNullable<typeof org>), logoUrl, brandColor: brandColor });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <Controller
        control={control}
        name="logoUrl"
        render={({ field: { value, onChange }, fieldState: { error } }) => {
          const validateImageDimensionsFromDataUrl = (dataUrl: string): Promise<string | null> => {
            const minDimension = 100;
            return new Promise((resolve) => {
              const img = new Image();
              img.src = dataUrl;
              img.onload = () => {
                if (img.width < minDimension || img.naturalHeight < minDimension) {
                  resolve(`File must be greater than ${minDimension}x${minDimension} pixels.`);
                } else {
                  resolve(null);
                }
              };
              img.onerror = () => resolve('Could not read image dimensions.');
            });
          };

          const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (file) {
              const maxSizeBytes = 5 * 1024 * 1024;
              if (file.size > maxSizeBytes) {
                toast.error('File exceeds 5MB.');
                return;
              }

              try {
                const dataUrl = await new Promise<string>((resolve, reject) => {
                  const reader = new FileReader();
                  reader.readAsDataURL(file);
                  reader.onload = () => resolve(reader.result as string);
                  reader.onerror = (error) => reject(error);
                });

                const dimensionError = await validateImageDimensionsFromDataUrl(dataUrl);
                if (dimensionError) {
                  toast.error(dimensionError);
                  return;
                }

                onChange(dataUrl);
              } catch (error) {
                console.error('Failed to read the logo image:', error);
                toast.error('Could not read that image. Please try a different file.');
              }
            }
          };

          const triggerFileInput = () => {
            fileInputRef.current?.click();
          };

          return (
            <Label className="flex items-start gap-1">
              <div className="w-1/3 shrink-0">Logo</div>
              <div className="flex flex-1 flex-col gap-2">
                {value && <img src={convertLogoUrl(value)} alt="Logo" className="h-auto max-h-[100px] w-fit max-w-[200px] object-contain" />}
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileSelect} className="hidden" />
                <div className="flex items-center gap-2">
                  <Button type="button" onClick={triggerFileInput} className="w-fit" disabled={!canEditWorkspaceSettings}>
                    Choose file
                  </Button>

                  <TooltipProvider>
                    <Tooltip delayDuration={0}>
                      <TooltipTrigger asChild>
                        <QuestionIcon />
                      </TooltipTrigger>
                      <TooltipPortal>
                        <TooltipContent side="right">
                          <TooltipArrow className="fill-white drop-shadow-[0_1px_0_rgb(var(--grey-3))]" />
                          <div className="">
                            <p>Logo must be:</p>
                            <ul className="list-disc pl-4 text-left">
                              <li>Less than 5MB</li>
                              <li>Greater than 100x100 pixels</li>
                              <li>Either a JPG, JPEG, PNG or BMP file</li>
                            </ul>
                          </div>
                        </TooltipContent>
                      </TooltipPortal>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                {error && <p className="text-default-red text-sm">Please select a valid image file</p>}
              </div>
            </Label>
          );
        }}
      />

      <Controller
        control={control}
        name="color"
        render={({ field: { value, onChange } }) => {
          return (
            <Label className="flex items-start gap-1">
              <div className="w-1/3 shrink-0">Brand colour</div>
              <div className="flex flex-1 flex-col gap-2">
                <Input
                  type="color"
                  containerClassName="w-fit"
                  className="[&::-webkit-color-swatch]:border-grey-3 h-10 w-9 cursor-pointer border-none bg-transparent p-0 [&::-webkit-color-swatch]:rounded [&::-webkit-color-swatch]:border-2"
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={!canEditWorkspaceSettings}
                />
              </div>
            </Label>
          );
        }}
      />

      {canEditWorkspaceSettings && (
        <div className="col-12">
          <Button type="submit" variant="success" className="mt-2" disabled={!isValid || isSubmitting || !isDirty} loading={isSubmitting}>
            Save changes
          </Button>
        </div>
      )}
    </form>
  );
}

export default SettingsBrandingPage;
